import tablesMetadataService from './tablesMetadataService';
import {
    DB_CONSTANTS,
    getCurrentVersion,
} from './dbHelpers/dbStorageHelper';

// Import helper classes
import tableManager from './dbHelpers/tableManager';
import recordManager from './dbHelpers/recordManager';
import dbMaintenance from './dbHelpers/dbMaintenance';
import syncHelper from './dbHelpers/syncHelper';
import bulkOperationsHelper from './dbHelpers/bulkOperationsHelper';


class StorageService {
    constructor() {
        this.db = null;
        this.isInitialized = false;
        this.currentVersion = getCurrentVersion();
    }

    async initialize() {
        if (this.isInitialized) return;

        try {
            // Check if we need to upgrade the database
            const requiredTables = tableManager.getAllRequiredTables();
            if (this.db) {
                const existingTables = Array.from(this.db.objectStoreNames);
                const needsUpgrade = requiredTables.some(table => !existingTables.includes(table));
                if (needsUpgrade) {
                    this.currentVersion++;
                    localStorage.setItem(DB_CONSTANTS.DB_VERSION_KEY, this.currentVersion.toString());
                    this.db.close();
                    this.db = null;
                }
            }

            this.db = await this.initializeDB();
            this.isInitialized = true;

            // Verify and create indexes on all tables
            await tableManager.ensureRequiredIndexes(this.db, () => this.initialize());

            // Ensure the Notification table has the userId index
            try {
                // Import the function dynamically to avoid circular dependencies
                const { ensureNotificationUserIdIndex, ensureUuidIndex } = await import('./dbHelpers/indexedDBUtils');

                // Check and create the userId index for Notification table
                const notificationIndexCreated = await ensureNotificationUserIdIndex(DB_CONSTANTS.DB_NAME);

                // Check and create uuid indexes for all tables
                const uuidIndexesCreated = await ensureUuidIndex(DB_CONSTANTS.DB_NAME);

                // If any index was created, we need to reinitialize to use the new version
                if (notificationIndexCreated || uuidIndexesCreated) {
                    // console.log('[dbStorageService] Indexes created, reinitializing database');
                    this.isInitialized = false;
                    this.db.close();
                    this.db = null;
                    await this.initialize();
                }
            } catch (indexError) {
                console.warn('[dbStorageService] Error ensuring indexes:', indexError);
                // Continue initialization even if index creation fails
            }
        } catch (error) {
            console.error('Failed to initialize storage service:', error);
            throw error;
        }
    }

    initializeDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_CONSTANTS.DB_NAME, this.currentVersion);

            request.onerror = () => reject(request.error);

            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                // Create stores for static tables
                this.createStores(db);
            };
        });
    }

    createStores(db) {
        // Create static tables
        tableManager.createDynamicStores(db);
    }

    async ensureTableExists(tableName) {
        // Skip Circle-related tables as they don't exist in current schema
        const circleTables = ['Circle', 'CircleMember', 'CircleOfTrust'];
        if (circleTables.includes(tableName)) {
            console.warn(`[dbStorageService] Skipping Circle table: ${tableName}`);
            return false;
        }

        await this.initialize();

        // Delegate to tableManager with initialize callback
        return tableManager.ensureTableExists(
            this.db,
            tableName,
            // Pass initialize callback
            async () => {
                this.isInitialized = false;
                await this.initialize();
                return this.db;
            }
        );
    }

    // Add a direct function that explicitly checks if a table exists without attempting a transaction
    tableExists(tableName) {
        if (!this.db) return false;
        return tableManager.tableExists(this.db, tableName);
    }

    // Add a more direct table creation function for emergency cases
    async createEmergencyTable(tableName) {
        // Explicitly skip Circle tables
        const circleTables = ['Circle', 'CircleMember', 'CircleOfTrust'];
        if (circleTables.includes(tableName)) {
            console.warn(`[dbStorageService] Preventing emergency creation of Circle table: ${tableName}`);
            return false;
        }

        try {
            return await tableManager.createEmergencyTable(
                this.db,
                tableName,
                async () => {
                    this.isInitialized = false;
                    await this.initialize();
                    return this.db;
                }
            );
        } catch (error) {
            console.error(`[dbStorageService] Error in createEmergencyTable for ${tableName}:`, error);
            return false;
        }
    }

    /**
     * Upserts records to the specified table
     * @param {string} tableName - Name of the table
     * @param {Array<Object>} records - Records to upsert
     * @param {Date} [currentSyncStartTime] - Current sync start time (for server updates)
     * @param {boolean} [isUserInitiated=true] - Whether the upsert was initiated by user action
     * @returns {Promise<Array>} - Upserted records
     */
    async upsertRecords(tableName, records, currentSyncStartTime, isUserInitiated = true) {
        if (!Array.isArray(records) || records.length === 0) {
            return [];
        }

        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        try {
            // First try to ensure table exists
            const wasUpgraded = await this.ensureTableExists(tableName);
            if (wasUpgraded) {
                // If database was upgraded, we need to reinitialize
                await this.initialize();
            }

            // Use the bulkOperationsHelper to perform the actual upsert
            return await bulkOperationsHelper.performUpsert(
                this.db,
                tableName,
                records,
                currentSyncStartTime,
                isUserInitiated
            );
        } catch (error) {
            console.error(`Error in upsertRecords for table ${tableName}:`, error);
            throw error;
        }
    }

    /**
     * Deletes records from a table by their UUIDs
     * @param {string} tableName - The name of the table
     * @param {Array<string>} uuids - Array of UUIDs to delete
     * @param {boolean} [isUserInitiated=false] - Whether this operation was initiated by user action
     * @returns {Promise<Array<string>>} - The deleted UUIDs
     */
    async deleteRecords(tableName, uuids, isUserInitiated = false) {
        if (!Array.isArray(uuids) || uuids.length === 0) {
            return [];
        }

        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        try {
            // First try to ensure table exists
            const wasUpgraded = await this.ensureTableExists(tableName);
            if (wasUpgraded) {
                // If database was upgraded, we need to reinitialize
                await this.initialize();
            }

            // Use the bulkOperationsHelper to perform the actual deletion
            return await bulkOperationsHelper.performDelete(
                this.db,
                tableName,
                uuids,
                isUserInitiated
            );
        } catch (error) {
            console.error(`Error in deleteRecords for table ${tableName}:`, error);
            throw error;
        }
    }

    /**
     * Marks records for deletion instead of actually deleting them
     * This is typically used for synced tables
     * @param {string} tableName - The name of the table
     * @param {Array<string>} uuids - Array of UUIDs to mark as deleted
     * @param {boolean} [isUserInitiated=false] - Whether this operation was initiated by user action
     * @returns {Promise<Array>} - The updated records
     */
    async markForDeletion(tableName, uuids, isUserInitiated = false) {
        if (!Array.isArray(uuids) || uuids.length === 0) {
            return [];
        }

        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        // CRITICAL CHECK: Make sure the table exists before any transaction attempt
        if (!this.tableExists(tableName)) {
            console.warn(`Table ${tableName} does not exist before marking for deletion. Creating it now.`);
            await this.createEmergencyTable(tableName);
        }

        // Ensure table exists before proceeding
        if (!this.tableExists(tableName)) {
            throw new Error(`Cannot mark records for deletion: Table ${tableName} does not exist`);
        }

        try {
            // Use the bulkOperationsHelper to perform the actual mark for deletion
            return await bulkOperationsHelper.performMarkForDeletion(
                this.db,
                tableName,
                uuids,
                isUserInitiated
            );
        } catch (transactionError) {
            console.error(`Failed to mark records for deletion in ${tableName}:`, transactionError);

            // If transaction creation fails, try one more emergency approach
            if (transactionError.name === 'NotFoundError') {
                await this.createEmergencyTable(tableName);
                // Try again with the new table
                return await bulkOperationsHelper.performMarkForDeletion(
                    this.db,
                    tableName,
                    uuids,
                    isUserInitiated
                );
            } else {
                throw transactionError;
            }
        }
    }

    async getAllRecords(tableName) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        // Ensure table exists before proceeding
        await this.ensureTableExists(tableName);

        const transaction = this.db.transaction(tableName, 'readonly');
        const store = transaction.objectStore(tableName);

        return new Promise((resolve, reject) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getRecordsByIndex(tableName, indexName, value) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return recordManager.getRecordsByIndex(this.db, tableName, indexName, value);
    }

    async getRecordsByField(tableName, fieldName, fieldValue) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return recordManager.getRecordsByField(this.db, tableName, fieldName, fieldValue);
    }

    /**
     * Gets records modified since the given timestamp
     * @param {string} tableName - Name of the table to query
     * @param {Date|string} timestamp - Timestamp to check against
     * @param {Object} options - Additional options for the query
     * @returns {Promise<Array>} Array of matching records
     */
    async getRecordsModifiedSince(tableName, timestamp, options = {}) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return recordManager.getRecordsModifiedSince(this.db, tableName, timestamp, options);
    }

    getSyncableTables() {
        return tablesMetadataService.GetAllSyncableTables();
    }

    async getLocalChanges() {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return syncHelper.getLocalChanges(this.db);
    }

    /**
     * Cleans up records marked as deleted after they have been synchronized with the server
     * @param {string} tableName - The name of the table to clean up
     * @param {Object} options - Options for cleanup
     * @returns {Promise<Object>} - Results of the cleanup operation
     */
    async cleanupSyncedDeletes(tableName, options = {}) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return recordManager.cleanupSyncedDeletes(this.db, tableName, options);
    }

    async getUnreadNotificationsCount() {
        await this.initialize();

        return syncHelper.getUnreadNotificationsCount(this.db);
    }

    async getLocalTables() {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return tableManager.getLocalTables(this.db);
    }

    /**
     * Check if the database is currently in the process of being cleaned up
     * Can be used by components to avoid accessing the database during logout
     * @returns {boolean} Whether cleanup is in progress
     */
    static isCleanupInProgress() {
        return dbMaintenance.isCleanupInProgress();
    }

    terminateBackgroundOperations() {
        dbMaintenance.terminateBackgroundOperations();
    }

    async cleanup() {
        return dbMaintenance.cleanup(this.db);
    }

    /**
     * Checks if a table has any records modified since the given timestamp
     * @param {string} tableName - Name of the table to check
     * @param {Date|string} timestamp - Timestamp to check against
     * @returns {Promise<boolean>} Whether the table has changes
     */
    async hasChangedRecordsSince(tableName, timestamp) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return recordManager.hasChangedRecordsSince(this.db, tableName, timestamp);
    }

    /**
     * Get records that have been changed after a given timestamp
     * Uses optimized query that limits fields returned
     * @param {string} tableName - Name of the table to query
     * @param {Date|string} timestamp - Timestamp to check changes since
     * @param {Object} options - Optional parameters
     * @returns {Promise<Array>} - Array of changed records
     */
    async getChangedRecordsSince(tableName, timestamp, options = {}) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return recordManager.getChangedRecordsSince(this.db, tableName, timestamp, options);
    }

    /**
     * Process a batch of updates for a specific table
     * @param {string} tableName - The name of the table
     * @param {Array} updates - Array of update objects
     * @returns {Promise<number>} - Number of successfully processed updates
     */
    async processBatchUpdates(tableName, updates) {
        await this.initialize();
        if (!this.db) throw new Error('Database not initialized');

        return recordManager.processBatchUpdates(this.db, tableName, updates);
    }
}

const storageService = new StorageService();
export default storageService;
